/**
 * Load/TONU cancellation + immediate reassignment — field names and defaults.
 *
 * Frontend-only workflow (Firestore client writes). Not used for super-admin account
 * cancellation (see backend/src/config/accountCancellationWorkflow.ts).
 *
 * Phase 0 locked spec: docs/CANCELLATION_REASSIGNMENT_PHASE0_AUDIT.md
 */

/** Firestore keys on loads / purchaseOrders / brokerPurchaseOrders */
export const CANCELLATION_REASSIGNMENT_FIELDS = {
  REASSIGN_AFTER_CANCELLATION: 'reassignAfterCancellation',
  /**
   * True after cancel+reassign submit until shipper/broker picks partner vs marketplace
   * on the dashboard “Select New Carrier” action. Blocks marketplace browse until cleared.
   */
  REASSIGN_CHANNEL_PENDING: 'reassignChannelPending',
  /** Set on new open load → prior pending-cancel assignment (Phase 2+) */
  SUPERSEDED_ASSIGNMENT_LOAD_ID: 'supersededAssignmentLoadId',
  /** Set on old assignment load → replacement listing (Phase 2+) */
  SUPERSEDED_BY_LOAD_ID: 'supersededByLoadId',
} as const;

export const CANCELLATION_REASSIGNMENT_DEFAULTS = {
  REASSIGN_AFTER_CANCELLATION: false,
} as const;

/** Notification metadata — reuse carrier_decline dashboard card in Phase 2 */
export const CANCELLATION_REASSIGNMENT_NOTIFICATION = {
  SOURCE_CANCELLATION: 'cancellation_reassign',
} as const;

export const CANCELLATION_REASSIGNMENT_MESSAGES = {
  dashboardAction: (poNumber: string) => {
    const label = poNumber.startsWith('PO-') ? poNumber : `PO ${poNumber}`;
    return `Load cancellation initiated for ${label}. Please select a new carrier.`;
  },
} as const;

/** PO awaits shipper/broker partner vs marketplace choice after cancel+reassign. */
export function isPoAwaitingCancellationReassignChannel(
  poData: Record<string, unknown> | null | undefined
): boolean {
  if (!poData) return false;
  if (poData[CANCELLATION_REASSIGNMENT_FIELDS.REASSIGN_AFTER_CANCELLATION] !== true) return false;
  if (poData[CANCELLATION_REASSIGNMENT_FIELDS.REASSIGN_CHANNEL_PENDING] === false) return false;
  const carrierId = poData.carrierId;
  const hasAssignedCarrier =
    carrierId != null && carrierId !== '';
  if (hasAssignedCarrier) return false;
  return true;
}

/** Cleared when shipper/broker completes dashboard reassignment channel selection. */
export function buildReassignChannelResolvedPoPatch(): Record<string, boolean> {
  return {
    [CANCELLATION_REASSIGNMENT_FIELDS.REASSIGN_CHANNEL_PENDING]: false,
  };
}
